import i18nFactory from '@yandex-int/i18n';
import { Epic } from 'redux-observable';
import { Observable } from 'rxjs';

import { Action } from '../../../actions';
import {
    changeLocationHard,
    updateDocumentTitle
} from '../../../actions/common';
import {
    FETCH_INITIAL_FREELANCER_DATA,
    FetchInitialFreelancerDataAction,
    FREELANCER_CARD_EDIT_IMAGE_UPLOAD_START,
    FreelancerCardEditAction,
    freelancerCardEditImageUploadError,
    FreelancerCardEditImageUploadStartAction,
    freelancerCardEditImageUploadSuccess,
    freelancerCardEditSetSaveProgressStatus,
    SAVE_FREELANCER_DATA,
    SaveFreelancerDataAction,
    setInitialFreelancerDataError,
    updateInitialFreelancerData
} from '../../../actions/freelancers/freelancer-card-edit';
import { NoticeType, showNoticeMessage } from '../../../actions/grid-screen';
import {
    mapInitialFreelancerInfoResponse,
    mapStateForFreelancerCardMutation,
    mapStateForFreelancerServicesMutation
} from '../../../mappers/freelancers';
import {
    FreelancerAccessError,
    FreelancerAccessErrorTypes
} from '../../../service/FreelancerAccessError';
import { EpicDeps } from '../../../service/StoreFactory';
import { UrlHelpers } from '../../../service/UrlHelpers';
import { State } from '../../../typings/state';

import * as keyset from './freelancer-card-edit.i18n';

const i18n = i18nFactory(keyset);

export const freelancerCardEditInitialFetchEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(FETCH_INITIAL_FREELANCER_DATA)
        .mergeMap((action: FetchInitialFreelancerDataAction) => {
            return Observable.fromPromise(
                deps.freelancerDataProvider.fetchInitialFreelancerData(
                    action.login
                )
            )
                .map(response =>
                    mapInitialFreelancerInfoResponse(response.data)
                )
                .mergeMap(freelancerData =>
                    Observable.concat(
                        Observable.of(
                            updateInitialFreelancerData(
                                freelancerData.editData,
                                freelancerData.servicesList,
                                freelancerData.certificates,
                                freelancerData.initRegion,
                                action.regionInputId
                            )
                        ),
                        Observable.of(
                            updateDocumentTitle(
                                i18n('pageHeader', {
                                    fullName: freelancerData.editData.fullName
                                })
                            )
                        )
                    )
                )
                .catch((e: FreelancerAccessError) =>
                    Observable.of<FreelancerCardEditAction>(
                        setInitialFreelancerDataError(
                            e.errorType ||
                                FreelancerAccessErrorTypes.NO_DATA_PROVIDED
                        )
                    )
                );
        });
};

export const updateFreelancerCardItemEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(SAVE_FREELANCER_DATA)
        .filter(() => store$.value.freelancerCardEdit.isDataReadyToSave)
        .mergeMap(
            (action: SaveFreelancerDataAction): Observable<Action> => {
                // geoId есть обязательно, иначе ключ isDataReadyToSave === false не разрешит исполнение эпика
                const { regions, freelancerCardEdit } = store$.value;

                const region = regions[action.regionInputId];
                const geoId = region.geoId!;

                return Observable.concat(
                    Observable.of(
                        freelancerCardEditSetSaveProgressStatus(true)
                    ),
                    Observable.fromPromise(
                        deps.freelancerMutationExecutor.updateFreelancerCard(
                            mapStateForFreelancerCardMutation(
                                freelancerCardEdit.edit,
                                freelancerCardEdit.avatarId
                            ),
                            mapStateForFreelancerServicesMutation(
                                freelancerCardEdit.edit
                            ),
                            geoId
                        )
                    )
                        .map(() => {
                            const login = store$.value.commonData.login;
                            return changeLocationHard(
                                UrlHelpers.getFreelancerCardUrl(login, login)
                            );
                        })
                        .catch(() => {
                            showNoticeMessage(
                                NoticeType.error,
                                i18n('saveError')
                            );
                            return Observable.of(
                                freelancerCardEditSetSaveProgressStatus(false)
                            );
                        })
                );
            }
        );
};

export const freelancerCardEditImageUploadEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(FREELANCER_CARD_EDIT_IMAGE_UPLOAD_START).mergeMap(
        (
            action: FreelancerCardEditImageUploadStartAction
        ): Observable<Action> => {
            return Observable.fromPromise(
                deps.restApiManager.uploadFreelancerImage(
                    action.login,
                    action.blob
                )
            )
                .mergeMap(avatar =>
                    Observable.of(freelancerCardEditImageUploadSuccess(avatar))
                )
                .catch(() =>
                    Observable.of(freelancerCardEditImageUploadError())
                );
        }
    );
};
